import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useTranslation from '../hooks/useTranslation';
import './Services.css';

/* ── Service data (icon + short tagline per card) ─────────────── */
const SERVICE_META = [
  {
    icon: '🎬',
    gradient: 'linear-gradient(135deg, #1a2744 0%, #2e4a8a 100%)',
    accentColor: '#D4A843',
  },
  {
    icon: '📢',
    gradient: 'linear-gradient(135deg, #2e1a44 0%, #5a2e8a 100%)',
    accentColor: '#D4A843',
  },
  {
    icon: '🎓',
    gradient: 'linear-gradient(135deg, #1a3a28 0%, #2e7a50 100%)',
    accentColor: '#D4A843',
  },
  {
    icon: '📰',
    gradient: 'linear-gradient(135deg, #44200e 0%, #8a3e1a 100%)',
    accentColor: '#D4A843',
  },
  {
    icon: '💡',
    gradient: 'linear-gradient(135deg, #1a2744 0%, #253660 100%)',
    accentColor: '#D4A843',
  },
];

/* ── Short taglines (hard-coded because locale has long desc) ─── */
const SHORT_TAGS = {
  ar: [
    'من الفكرة إلى البث',
    'حضور يصنع التأثير',
    'نُطوّر المواهب الإبداعية',
    'نضع رسالتك في مكانها',
    'أداء إعلامي أكثر تأثيراً',
  ],
  en: [
    'From idea to broadcast',
    'Presence that makes impact',
    'We develop creative talent',
    'Your message, perfectly placed',
    'Sharper, clearer media performance',
  ],
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const Services = () => {
  const { t, lang, isRTL } = useTranslation();
  const items = t('services.items');
  const [expanded, setExpanded] = useState(null);

  if (!Array.isArray(items)) return null;

  return (
    <section id="services" className="services" aria-label={isRTL ? 'خدماتنا' : 'Our Services'}>
      <div className="container">

        {/* ── Heading ── */}
        <motion.div
          className="section-heading"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="services__label">{isRTL ? 'ما نقدّمه' : 'What We Offer'}</span>
          <h2>{t('services.title')}</h2>
          <p>{t('services.subtitle')}</p>
        </motion.div>

        {/* ── Cards Grid ── */}
        <motion.div
          className="services__grid"
          role="list"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
        >
          {items.map((item, i) => {
            const meta = SERVICE_META[i] || SERVICE_META[0];
            const tag = SHORT_TAGS[lang]?.[i] ?? '';
            const isOpen = expanded === i;

            return (
              <motion.article
                key={i}
                className={`services__card${isOpen ? ' services__card--open' : ''}`}
                role="listitem"
                variants={cardVariants}
                layout
              >
                {/* Colored top bar */}
                <div className="services__card-bar" style={{ background: meta.gradient }} />

                {/* Icon circle */}
                <div className="services__icon-wrap" style={{ background: meta.gradient }}>
                  <span className="services__icon" aria-hidden="true">{meta.icon}</span>
                </div>

                {/* Content */}
                <h3 className="services__card-title">{item.title}</h3>
                <p className="services__card-tag">{tag}</p>

                {/* Expand toggle */}
                <button
                  className="services__toggle"
                  onClick={() => setExpanded(isOpen ? null : i)}
                  aria-expanded={isOpen}
                  aria-label={isOpen
                    ? (isRTL ? 'إخفاء التفاصيل' : 'Hide details')
                    : (isRTL ? 'عرض التفاصيل' : 'Show details')}
                >
                  <span>{isOpen ? (isRTL ? 'إخفاء' : 'Less') : (isRTL ? 'التفاصيل' : 'Details')}</span>
                  <svg
                    className={`services__chevron${isOpen ? ' services__chevron--up' : ''}`}
                    viewBox="0 0 24 24" width="14" height="14" fill="none"
                    stroke="currentColor" strokeWidth="2.5"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {/* Expandable list */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.ul
                      className="services__list"
                      aria-label={item.title}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.35, ease: [0.4, 0, 0.2, 1] }}
                    >
                      {Array.isArray(item.items) && item.items.map((li, j) => (
                        <li key={j} className="services__list-item">
                          <span className="services__bullet" aria-hidden="true" />
                          {li}
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
